import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { Box, Inline } from '@atlaskit/primitives';
import { ActionName, SmartLinkDirection, SmartLinkSize, SmartLinkWidth } from '../../../../../../constants';
import ActionGroup from '../../action-group';
import Block from '../../block';
import ElementGroup from '../../element-group';
import MotionWrapper from '../../../common/motion-wrapper';
import AIStateIndicator from '../ai-state-indicator';
import { renderElementItems } from '../../utils';
import { messages } from '../../../../../../messages';
import { useAISummary } from '../../../../../../state/hooks/use-ai-summary';
import AIIcon from '../../../../../common/ai-icon';
import AISummary from '../../../../../common/ai-summary';
import { useAnalyticsEvents } from '../../../../../../common/analytics/generated/use-analytics-events';
import AIEventSummaryViewed from '../ai-event-summary-viewed';
import AIEventErrorViewed from '../../../common/ai-summary/ai-event-error-viewed';
import { di } from 'react-magnetic-di';
import { css } from '@emotion/react';
import FeatureDiscovery from '../feature-discovery';
import { useFlexibleUiContext } from '../../../../../../state/flexible-ui-context';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
export const AISummaryBlockErrorIndicator = ({
  showErrorIndicator,
  error,
  showTransition,
  testId
}) => /*#__PURE__*/React.createElement(MotionWrapper, {
  isFadeIn: true,
  show: showErrorIndicator,
  showTransition: showTransition
}, /*#__PURE__*/React.createElement(AIEventErrorViewed, {
  reason: error
}), /*#__PURE__*/React.createElement(AIStateIndicator, {
  error: error,
  state: "error",
  testId: testId
}));
export const AISummaryBlockStatusIndicator = ({
  showStatusIndicator,
  metadata,
  status,
  testId
}) => {
  const metadataElements = renderElementItems(metadata);
  return showStatusIndicator ? /*#__PURE__*/React.createElement(AIStateIndicator, {
    state: status,
    testId: `${testId}-state-indicator`
  }) : /*#__PURE__*/React.createElement(ElementGroup, {
    width: SmartLinkWidth.FitToContent,
    testId: `${testId}-metadata-group`
  }, metadataElements);
};
const AISummaryBlockResolvedView = props => {
  const {
    actions = [],
    onActionMenuOpenChange,
    size = SmartLinkSize.Medium,
    testId,
    aiSummaryMinHeight = 0,
    metadata,
    placeholder
  } = props;
  const {
    fireEvent
  } = useAnalyticsEvents();
  const context = useFlexibleUiContext();
  const url = (context === null || context === void 0 ? void 0 : context.url) || '';
  const ari = (context === null || context === void 0 ? void 0 : context.ari) || '';
  const {
    product,
    connections
  } = useSmartLinkContext();
  const {
    summariseUrl,
    state: {
      content,
      status,
      error
    }
  } = useAISummary({
    url,
    ari,
    product,
    envKey: connections.client.envKey,
    baseUrl: connections.client.baseUrlOverride
  });
  const showAISummary = status === 'done' ||
  // We want to display the AI Summary component only when there is content available during the loading process.
  status === 'loading' && !!content;
  const isSummarisedOnMountRef = useRef(status === 'done');
  const isErroredOnMountRef = useRef(status === 'error');
  const minHeight = getBooleanFF('platform.linking-platform.smart-card.hover-card-action-redesign') && isSummarisedOnMountRef.current ? 0 : aiSummaryMinHeight;
  useEffect(() => {
    // if the error was apparent on mount and the status is changed to loading we can
    // clear the initial error on mount state
    if (isErroredOnMountRef.current && status === 'loading') {
      isErroredOnMountRef.current = false;
    }
  }, [status]);
  const combinedActions = useMemo(() => {
    if (status === 'ready' || status === 'error') {
      const aiAction = {
        content: /*#__PURE__*/React.createElement(FormattedMessage, messages.ai_summarize),
        name: ActionName.CustomAction,
        onClick: () => {
          fireEvent('ui.button.clicked.aiSummary', {});
          fireEvent('track.aiInteraction.initiated', {
            aiFeatureName: 'Smart Links Summary',
            proactiveAIGenerated: 0,
            userGeneratedAI: 1
          });
          summariseUrl();
        },
        testId: `${testId}-ai-summary-action`,
        icon: /*#__PURE__*/React.createElement(AIIcon, {
          label: "AIIcon"
        }),
        /**
         * Enabling feature discovery pulse
         * Cleanup: https://product-fabric.atlassian.net/browse/EDM-9693
         */
        wrapper: FeatureDiscovery
      };
      return [aiAction, ...actions];
    }
    return actions;
  }, [actions, status, testId, summariseUrl, fireEvent]);
  const onDropdownOpenChange = useCallback(isOpen => {
    if (onActionMenuOpenChange) {
      onActionMenuOpenChange({
        isOpen
      });
    }
  }, [onActionMenuOpenChange]);
  if (!showAISummary && getBooleanFF('platform.linking-platform.smart-card.hover-card-action-redesign')) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, placeholder);
  }
  return /*#__PURE__*/React.createElement(Block, _extends({}, props, {
    direction: SmartLinkDirection.Vertical,
    testId: `${testId}-resolved-view`
    /**
     * Enabled for feature discovery to allow box shadow to overflow
     * Cleanup: https://product-fabric.atlassian.net/browse/EDM-8681
     */,
    overrideCss: css({
      overflow: 'visible'
    }, props.overrideCss)
  }), status === 'done' && /*#__PURE__*/React.createElement(AIEventSummaryViewed, {
    fromCache: isSummarisedOnMountRef.current
  }), /*#__PURE__*/React.createElement(MotionWrapper, {
    minHeight: minHeight,
    show: showAISummary,
    showTransition: !isSummarisedOnMountRef.current
  }, /*#__PURE__*/React.createElement(AISummary, {
    testId: `${testId}-ai-summary`,
    minHeight: minHeight,
    content: content,
    showIcon: !getBooleanFF('platform.linking-platform.smart-card.hover-card-action-redesign') && isSummarisedOnMountRef.current
  })), !getBooleanFF('platform.linking-platform.smart-card.hover-card-action-redesign') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    alignInline: "end",
    grow: "fill",
    spread: "space-between",
    testId: `${testId}-footer-metadata`
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AISummaryBlockStatusIndicator, {
    metadata: metadata,
    showStatusIndicator: !isSummarisedOnMountRef.current && (status === 'loading' || status === 'done'),
    status: status,
    testId: testId
  })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ActionGroup, {
    onDropdownOpenChange: onDropdownOpenChange,
    items: combinedActions,
    appearance: "default",
    size: size
  }))), /*#__PURE__*/React.createElement(AISummaryBlockErrorIndicator, {
    showErrorIndicator: !isErroredOnMountRef.current && status === 'error',
    showTransition: !isErroredOnMountRef.current,
    error: error,
    testId: `${testId}-error-indicator`
  })));
};
export default AISummaryBlockResolvedView;